import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { AppConfigurationVersion } from "@/types/application";

interface RollbackConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  version?: AppConfigurationVersion;
}

export const RollbackConfirmationDialog = ({
  open,
  onOpenChange,
  onConfirm,
  version
}: RollbackConfirmationDialogProps) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm();
      setConfirmed(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmed(false);
    }
    onOpenChange(newOpen);
  };

  if (!version) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Confirm Rollback
          </DialogTitle>
          <DialogDescription>
            You are about to rollback to version {version.versionNumber} of this configuration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <h4 className="font-medium">Version Details:</h4>
            <div className="text-sm space-y-1">
              <div>
                <span className="text-muted-foreground">Name:</span> {version.name}
              </div>
              <div>
                <span className="text-muted-foreground">Keys:</span>{' '}
                {version.configurationData.keys.length}
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>{' '}
                {version.isActive ? 'Active' : 'Inactive'}
              </div>
              {version.changeSummary && (
                <div>
                  <span className="text-muted-foreground">Changes:</span> {version.changeSummary}
                </div>
              )}
            </div>
          </div>

          <div className="bg-warning/10 dark:bg-yellow-950/20 border border-warning/20 dark:border-warning p-4 rounded-lg">
            <p className="text-sm text-warning dark:text-warning">
              <strong>Warning:</strong> This action will overwrite the current configuration
              with the values from version {version.versionNumber}. The current state will be
              saved as a new version before applying the rollback.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="rollback-confirm"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked as boolean)}
            />
            <Label htmlFor="rollback-confirm" className="text-sm cursor-pointer">
              I understand this will overwrite the current configuration
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!confirmed}
            variant="destructive"
          >
            Confirm Rollback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
